<script>
  import { onMount } from "svelte";
  import { writable } from "svelte/store";
  import {
    SvelteFlow,
    ConnectionMode,
    MarkerType,
    useSvelteFlow,
    MiniMap,
  } from "@xyflow/svelte";

  import "@xyflow/svelte/dist/style.css";
  import { dagre } from "dagre-d3";
  import * as d3 from "d3";
  import CustomNode from "./CustomNode.svelte";
  import CustomEdge from "./CustomEdge.svelte";

  const nodeIcon = "/img/processModellerIcons/process.png";
  const startIcon = "/img/processModellerIcons/startProcess.png";
  const endIcon = "/img/processModellerIcons/endProcess.png";

  export let nodesData = [];
  export let edgesData = [];
  export let width = "500px";
  export let height = "500px";
  export let selectedNode = "";
  export let contextMenuEnabled = false;
  export let minFrequency = 0;
  export let seeMinimap = false;
  export let nvariants = 1;
  export let updateZoomLevel;

  export async function downloadSVG() {
    const flowContainer = document.querySelector(".svelte-flow");
    if (!flowContainer) return;

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");

    // Get viewport transform
    const viewport = flowContainer.querySelector(".svelte-flow__viewport");
    const transform = viewport ? viewport.style.transform : "";

    const translateMatch = transform.match(/translate\(([^)]+)\)/);
    const scaleMatch = transform.match(/scale\(([^)]+)\)/);

    const [translateX = 0, translateY = 0] = translateMatch
      ? translateMatch[1].split(",").map((v) => parseFloat(v))
      : [0, 0];
    const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;

    // Calculate SVG dimensions
    const nodes = flowContainer.querySelectorAll(".svelte-flow__node");
    const edgeLabels = flowContainer.querySelectorAll(".edge-label");
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    // Include calculation-centric nodes
    nodes.forEach((node) => {
      const transform = node.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1].split(",").map((v) => parseFloat(v));

        const nodeRect = node.getBoundingClientRect();
        const actualWidth = nodeRect.width + 4;
        const actualHeight = 60 + 4;

        const centeredX = x - actualWidth / 2 + actualWidth * 1.2;

        minX = Math.min(minX, centeredX);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, centeredX + actualWidth);
        maxY = Math.max(maxY, y + actualHeight);
      }
    });

    // Include edge labels in the calculation
    edgeLabels.forEach((label) => {
      const transform = label.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1]
          .split(",")
          .map((v) => parseFloat(v.replace("px", "")));
        const labelRect = label.getBoundingClientRect();
        const labelWidth = labelRect.width;

        minX = Math.min(minX, x - labelWidth / 2);
        minY = Math.min(minY, y - 20);
        maxX = Math.max(maxX, x + labelWidth / 2);
        maxY = Math.max(maxY, y + 60 + 20);
      }
    });

    const padding = 100;
    const svgWidth =
      maxX - minX + padding * 2 > 1000 ? maxX - minX + padding * 2 : 1000;
    const svgHeight = maxY - minY + padding * 2;

    svg.setAttribute("width", svgWidth);
    svg.setAttribute("height", svgHeight);
    svg.setAttribute("viewBox", `0 0 ${svgWidth} ${svgHeight}`);

    // white background
    const bg = document.createElementNS(svgNS, "rect");
    bg.setAttribute("width", "100%");
    bg.setAttribute("height", "100%");
    bg.setAttribute("fill", "white");
    svg.appendChild(bg);

    // Get styles
    const targetClasses = [
      ".svelte-flow",
      ".custom-node",
      ".container",
      ".label-node",
      ".edge-label",
      ".start-end-node",
      ".more_filled_icon",
      "button",
      ".svelte-flow__edges",
      ".svelte-flow__edge-path",
    ];
    const extractedStyles = extractClassStyles(targetClasses);

    // Add additional styles
    const style = document.createElementNS(svgNS, "style");
    style.textContent = `
      ${extractedStyles}
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      letter-spacing: 0.16px;
      font-size: 0.875rem;
      font-weight:400;
      line-height: 1.28572;
      line-height: 1;
      box-sizing: border-box !important;
    }
      foreignObject {
        overflow: visible;
      }
      .custom-node {
         padding: 20px 20px !important;

        }

      .custom-node.start-end-node {
        gap: 0;
      }
    `;
    svg.appendChild(style);

    // Convert images to base64
    const imagePromises = [];
    nodes.forEach((node) => {
      const images = node.querySelectorAll("img");
      images.forEach((img) => {
        if (img.src && !img.src.startsWith("data:")) {
          const promise = fetch(img.src)
            .then((response) => response?.ok ? response.blob() : null)
            .then(
              (blob) =>
                blob && new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = () => {
                    img.src = reader.result;
                    resolve();
                  };
                  reader.readAsDataURL(blob);
                })
            )
            .catch(() => console.warn("Could not convert image:", img.src));

          imagePromises.push(promise);
        }
      });
    });

    await Promise.all(imagePromises);

    // print main group with offset
    const mainGroup = document.createElementNS(svgNS, "g");
    mainGroup.setAttribute(
      "transform",
      `translate(${padding - minX}, ${padding - minY})`
    );

    // copy markers
    const defs = document.createElementNS(svgNS, "defs");
    const markers = flowContainer.querySelectorAll(
      ".svelte-flow__marker marker"
    );
    markers.forEach((marker) => {
      defs.appendChild(marker.cloneNode(true));
    });
    svg.appendChild(defs);

    // copy nodes
    nodes.forEach((node) => {
      const transform = node.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (!match) return;

      const [x, y] = match[1].split(",").map((v) => parseFloat(v));

      const nodeRect = node.getBoundingClientRect();
      const actualWidth = nodeRect.width;
      const actualHeight = nodeRect.height;

      const foreignObject = document.createElementNS(svgNS, "foreignObject");
      foreignObject.setAttribute("x", x);
      foreignObject.setAttribute("y", y);
      foreignObject.setAttribute("width", actualWidth);
      foreignObject.setAttribute("height", actualHeight);

      const nodeContent = node.querySelector(".container");
      if (nodeContent) {
        const contentClone = nodeContent.cloneNode(true);
        foreignObject.appendChild(contentClone);
        mainGroup.appendChild(foreignObject);
      }
    });

    // Create SVG group for edges with class (rendered on top)
    const edgesGroup = document.createElementNS(svgNS, "g");
    edgesGroup.setAttribute("class", "svelte-flow__edges");

    // Copy edges paths
    const edges = flowContainer.querySelectorAll(
      ".svelte-flow__edge path.svelte-flow__edge-path"
    );
    edges.forEach((edge) => {
      const pathClone = edge.cloneNode(true);
      edgesGroup.appendChild(pathClone);
    });

    mainGroup.appendChild(edgesGroup);

    // Copy edge labels
    edgeLabels.forEach((label) => {
      const transform = label.style.transform;
      const match = transform.match(/translate\(([^)]+)\)/);
      if (match) {
        const [x, y] = match[1]
          .split(",")
          .map((v) => parseFloat(v.replace("px", "")));

        const labelRect = label.getBoundingClientRect();
        const labelWidth = labelRect.width;
        const labelHeight = labelRect.height;

        const foreignObject = document.createElementNS(svgNS, "foreignObject");
        foreignObject.setAttribute("x", x + 50);
        foreignObject.setAttribute("y", y + 50);
        foreignObject.setAttribute("width", labelWidth);
        foreignObject.setAttribute("height", labelHeight);

        const labelClone = label.cloneNode(true);

        foreignObject.appendChild(labelClone);
        mainGroup.appendChild(foreignObject);
      }
    });

    svg.appendChild(mainGroup);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "cases-process-explorer.svg";
    a.click();

    URL.revokeObjectURL(url);
  }

  $$restProps;
  let contextMenu;
  let container;
  const { getZoom, viewport } = useSvelteFlow();

  let zoomLevel = writable((getZoom() * 100).toFixed(0));

  const nodes = writable([]);
  const edges = writable([]);
  let extrainfo = false;
  let contextMenuVisible = false;
  let subprocessMenuOpen = false;

  const nodeTypes = {
    custom: CustomNode,
  };

  const edgeTypes = {
    customEdg: CustomEdge,
  };

  $: {
    if ($viewport.zoom != zoomLevel) {
      updateZoomLevel();
    }
  }

  $: {
    {
      nodesData, edgesData, minFrequency, nvariants;
    }
    setData();
  }

  function calculateNodeWidth(label) {
    const padding = 32;
    const border = 4;
    const gap = 8;

    const segments = label.split("<br>");
    let maxWidth = 0;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = "14px Arial";

    segments.forEach((segment) => {
      const textWidth = ctx.measureText(segment).width;
      if (textWidth > maxWidth) {
        maxWidth = textWidth;
      }
    });

    const maxTextWidth = 340;
    const actualTextWidth = Math.min(maxWidth, maxTextWidth);

    const totalWidth = actualTextWidth + padding + border + gap;

    return totalWidth;
  }

  function calculateNodeHeight(label) {
    const baseHeight = 62;
    const lineHeight = 6;

    const lines = label.split("<br>").length;
    const totalHeight = baseHeight + (lines - 1) * lineHeight;
    return totalHeight;
  }

  function filterGraphByFrequency(nodesIn, edgesIn, minFrequency, nvariants) {
    const nodes = [...nodesIn];
    let allEdges = [...edgesIn];

    allEdges = allEdges.filter((e) => e.from !== e.to);
    allEdges = allEdges.sort((a, b) => a.value - b.value);

    const variantEdges = [...allEdges];
    let G = [...variantEdges];

    const startNodes = [];
    const endNodes = [];
    const nodeIds = new Set();

    G.forEach((edge) => {
      nodeIds.add(edge.from);
      nodeIds.add(edge.to);
    });

    nodeIds.forEach((nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      if (
        nodeId.includes("Start") ||
        (node.label && node.label.includes("Start"))
      ) {
        startNodes.push(nodeId);
      }
      if (
        nodeId.includes("End") ||
        (node.label && node.label.includes("End"))
      ) {
        endNodes.push(nodeId);
      }
    });

    if (startNodes.length === 0 || endNodes.length === 0) {
      console.warn("No start or end nodes found.");
      return { nodes: nodesIn.slice(), edges: edgesIn.slice() };
    }

    function localNodeConnectivity(fromId, toId, edgeList) {
      if (fromId === toId) return true;

      const visited = new Set();
      const queue = [fromId];
      const adjacency = new Map();

      const allNodeIds = new Set();
      edgeList.forEach((e) => {
        allNodeIds.add(e.from);
        allNodeIds.add(e.to);
      });

      allNodeIds.forEach((nodeId) => adjacency.set(nodeId, []));
      edgeList.forEach((e) => {
        adjacency.get(e.from).push(e.to);
      });

      while (queue.length > 0) {
        const current = queue.shift();
        if (current === toId) return true;
        if (visited.has(current)) continue;

        visited.add(current);
        const neighbors = adjacency.get(current) || [];
        neighbors.forEach((neighbor) => {
          if (!visited.has(neighbor)) {
            queue.push(neighbor);
          }
        });
      }

      return false;
    }

    let GTemp = [...G];

    for (const edge of variantEdges) {
      if (edge.value < minFrequency) {
        const tempEdgesWithoutCurrent = GTemp.filter(
          (e) => !(e.from === edge.from && e.to === edge.to)
        );

        let shouldKeepEdge = false;
        for (const startNode of startNodes) {
          for (const endNode of endNodes) {
            if (
              !localNodeConnectivity(
                startNode,
                endNode,
                tempEdgesWithoutCurrent
              )
            ) {
              shouldKeepEdge = true;
              break;
            }
          }
          if (shouldKeepEdge) break;
        }

        if (!shouldKeepEdge) {
          GTemp = tempEdgesWithoutCurrent;
        }
      }
    }

    let newEdges = [...variantEdges];

    const tempNodeIds = new Set();
    GTemp.forEach((edge) => {
      tempNodeIds.add(edge.from);
      tempNodeIds.add(edge.to);
    });

    for (const nodeId of tempNodeIds) {
      if (!nodeId.includes("Start") && !nodeId.includes("End")) {
        const cstart = startNodes.some((startNode) =>
          localNodeConnectivity(startNode, nodeId, GTemp)
        );
        const cend = endNodes.some((endNode) =>
          localNodeConnectivity(nodeId, endNode, GTemp)
        );

        if (!cstart && !cend) {
          G = G.filter((e) => e.from !== nodeId && e.to !== nodeId);
          newEdges = newEdges.filter(
            (e) => e.from !== nodeId && e.to !== nodeId
          );
        }
      }
    }

    for (const edge of [...newEdges]) {
      const edgeStillExists = G.some(
        (e) => e.from === edge.from && e.to === edge.to
      );

      if (edgeStillExists && edge.value < minFrequency) {
        const tempG = G.filter(
          (e) => !(e.from === edge.from && e.to === edge.to)
        );

        let canSafelyRemove = true;

        for (const startNode of startNodes) {
          if (!localNodeConnectivity(startNode, edge.to, tempG)) {
            canSafelyRemove = false;
            break;
          }
        }

        if (canSafelyRemove) {
          for (const endNode of endNodes) {
            if (!localNodeConnectivity(edge.from, endNode, tempG)) {
              canSafelyRemove = false;
              break;
            }
          }
        }

        if (canSafelyRemove) {
          G = tempG;
        }
      }
    }

    const connectedNodeIds = new Set();
    G.forEach((edge) => {
      connectedNodeIds.add(edge.from);
      connectedNodeIds.add(edge.to);
    });

    if (G.length > 0) {
      startNodes.forEach((nodeId) => connectedNodeIds.add(nodeId));
      endNodes.forEach((nodeId) => connectedNodeIds.add(nodeId));
    }

    const filteredNodes = nodes.filter((n) => connectedNodeIds.has(n.id));

    return { nodes: filteredNodes, edges: G };
  }

  function calculateNodePositions(nodes, edges, minFrequency) {
    const g = new dagre.graphlib.Graph();
    g.setGraph({
      rankdir: "TB",
      nodesep: 350,
      ranksep: 125,
    });
    g.setDefaultEdgeLabel(() => ({}));

    let { nodes: filteredNodes, edges: filteredEdges } = filterGraphByFrequency(
      nodes,
      edges,
      minFrequency,
      nvariants
    );
    nodes = filteredNodes;
    edges = filteredEdges;

    nodes.forEach((node) => {
      let labelContent = node.label;
      const width = calculateNodeWidth(labelContent);
      const height = calculateNodeHeight(labelContent);
      if (node.label === "Start" || node.label === "End") {
        g.setNode(node.id, {
          label: labelContent,
          width: calculateNodeWidth(labelContent),
          height: height,
          style: node.color ? `fill: ${node.color}` : "",
        });
      } else {
        g.setNode(node.id, {
          label: labelContent,
          width: width,
          height: height,
          style: node.color ? `fill: ${node.color}` : "",
        });
      }
    });

    edges.forEach(function (edge) {
      if (!g.edge(edge.from, edge.to)) {
        g.setEdge(edge.from, edge.to, {
          label: edge.value,
          curve: d3.curveMonotoneY,
          minlen: edge.from == "Start" || edge.to == "End" ? 2 : 1,
          weight: edge.from == "Start" || edge.to == "End" ? 1 : 200,
        });
      }
    });

    dagre.layout(g);

    const positionedNodes = nodes.map((node) => {
      const dagreNode = g.node(node.id);
      return {
        ...node,
        position: {
          x: dagreNode.x - 200,
          y: dagreNode.y - dagreNode.height / 2,
        },
      };
    });

    return {
      positionedNodes,
      edges: g.edges().map((e) => {
        const edge = g.edge(e);
        const points = edge.points;
        return {
          id: `${e.v}-${e.w}`,
          source: e.v,
          target: e.w,
          label: edge.label,
          points: points,
        };
      }),
    };
  }

  function normalizePozition(mouseX, mouseY, target) {
    // ? compute what is the mouse position relative to the container element (scope)
    let { left: scopeOffsetX, top: scopeOffsetY } =
      target.getBoundingClientRect();
    scopeOffsetX = scopeOffsetX < 0 ? 0 : scopeOffsetX;
    scopeOffsetY = scopeOffsetY < 0 ? 0 : scopeOffsetY;
    const scopeX = mouseX - scopeOffsetX;
    const scopeY = mouseY - scopeOffsetY;
    // ? check if the element will go out of bounds
    const outOfBoundsOnX =
      scopeX + contextMenu.clientWidth > target.clientWidth;
    const outOfBoundsOnY =
      scopeY + contextMenu.clientHeight > target.clientHeight;
    let normalizedX = mouseX;
    let normalizedY = mouseY;
    // ? normalize on X
    if (outOfBoundsOnX) {
      normalizedX = scopeOffsetX + target.clientWidth - contextMenu.clientWidth;
    }
    // ? normalize on Y
    if (outOfBoundsOnY) {
      normalizedY =
        scopeOffsetY + target.clientHeight - contextMenu.clientHeight;
    }
    return { normalizedX, normalizedY };
  }

  function openContextMenu(e, nodeid) {
    if (contextMenuEnabled) {
      e.preventDefault();
      if (!nodeid.match(/^(Start|End)( \d+)?$/gm)) {
        let { clientX: mouseX, clientY: mouseY, target: target } = e;
        let { normalizedX, normalizedY } = normalizePozition(
          mouseX,
          mouseY,
          target
        );
        contextMenuVisible = false;
        contextMenu.style.top = `${normalizedY}px`;
        contextMenu.style.left = `${normalizedX}px`;
        selectedNode = nodeid;
        setTimeout(() => {
          contextMenuVisible = true;
          extrainfo = false;
          subprocessMenuOpen = false;
        });
      }
    }
  }

  function hideContextMenu(e) {
    // ? close the menu if the user clicks outside of it
    if (e.target.offsetParent != contextMenu) {
      contextMenuVisible = false;
      subprocessMenuOpen = false;
    }
  }

  function toggleInfo(evt, d) {
    if (!d.match(/^(Start|End)( \d+)?$/gm)) {
      if (selectedNode === d) {
        selectedNode = "";
      } else {
        selectedNode = d;
      }
    }
  }

  var mouseleave = function (id) {
    edgesData
      .filter((edge) => edge.from === id || edge.to === id)
      .forEach((edge) => {
        const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
        const edgeElement = document.querySelector(edgeSelector);
        if (!edgeElement) return;
        const pathElement = edgeElement.querySelector("path");
        if (pathElement) {
          pathElement.classList.remove("edge_hovered");
          let markerId = pathElement.getAttribute("marker-end");
          markerId = markerId.replace("url('#", "").replace("')", "");

          let markedEnd = document.getElementById(markerId);
          if (markedEnd) {
            const polyline = markedEnd.querySelector("polyline");
            if (polyline) {
              polyline.setAttribute("stroke", "black");
              polyline.setAttribute("fill", "black");
            }
          }
        }

        const nextNode = document.querySelector(`[data-node-id="${edge.to}"]`);
        const prevNode = document.querySelector(
          `[data-node-id="${edge.from}"]`
        );
        if (nextNode) nextNode.classList.remove("node_hovered");
        if (prevNode) prevNode.classList.remove("node_hovered");
      });
  };

  function mouseover(evt, id) {
    document
      .querySelectorAll(".node_hovered")
      .forEach((el) => el.classList.remove("node_hovered"));
    document
      .querySelectorAll(".edge_hovered")
      .forEach((el) => el.classList.remove("edge_hovered"));

    edgesData
      .filter((edge) => edge.from === id || edge.to === id)
      .forEach((edge) => {
        const edgeSelector = `[data-id="${edge.from}-${edge.to}"]`;
        const edgeElement = document.querySelector(edgeSelector);
        if (!edgeElement) return;
        const pathElement = edgeElement.querySelector("path");
        if (pathElement) {
          pathElement.classList.add("edge_hovered");
          let markerId = pathElement.getAttribute("marker-end");
          markerId = markerId.replace("url('#", "").replace("')", "");

          let markedEnd = document.getElementById(markerId);
          if (markedEnd) {
            const polyline = markedEnd.querySelector("polyline");
            if (polyline) {
              polyline.setAttribute("stroke", "#ff5f1f");
              polyline.setAttribute("fill", "#ff5f1f");
            }
          }
        }
        const nextNode = document.querySelector(`[data-node-id="${edge.to}"]`);
        const prevNode = document.querySelector(
          `[data-node-id="${edge.from}"]`
        );
        if (nextNode) nextNode.classList.add("node_hovered");
        if (prevNode) prevNode.classList.add("node_hovered");
      });

    const currentNode = document.querySelector(`[data-node-id="${id}"]`);
    if (currentNode && !id.includes("_cluster"))
      currentNode.classList.add("node_hovered");
  }

  function resetData() {
    return new Promise((resolve) => {
      nodes.set([]);
      edges.set([]);
      resolve();
    });
  }

  function setData() {
    const { positionedNodes, edges: positionedEdges } = calculateNodePositions(
      nodesData,
      edgesData,
      minFrequency,
      nvariants
    );

    nodes.set(
      positionedNodes.map((node) => ({
        id: node.id,
        data: {
          label: node.label,
          icon: node.label.match(/^Start( \d+)?$/gm)
            ? startIcon
            : node.label.match(/^End( \d+)?$/gm)
              ? endIcon
              : nodeIcon,
          isStart: node.label.match(/^Start( \d+)?$/gm) ? true : false,
          isEnd: node.label.match(/^End( \d+)?$/gm) ? true : false,
          isSelected: node.id === selectedNode,
          position: node.position,
          seeIcon: false,
          seeButton: false,
          color: node.color,
        },
        position: node.position,
        style: node.color ? `fill: ${node.color}` : "",
        type: "custom",
        dragging: false,
      }))
    );

    edges.set(
      positionedEdges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: "customEdg",
        markerEnd: {
          data_id: edge.id + "_marker",
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 30,
          color: "black",
        },
        data: {
          points: edge.points,
          label: edge.label,
          casesView: true,
        },
      }))
    );
  }

  function extractClassStyles(classNames) {
    let cssText = "";
    const styleSheets = Array.from(document.styleSheets);

    styleSheets.forEach((sheet) => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules);
        rules.forEach((rule) => {
          if (rule.type === CSSRule.STYLE_RULE && rule.selectorText) {
            // Check exact match with target classes
            const matchesTarget = classNames.some((className) => {
              // Check if the selector is the class or contains it as a whole class
              return (
                rule.selectorText === className ||
                rule.selectorText.startsWith(`${className}.svelte`)
              );
            });

            if (matchesTarget) {
              cssText += rule.cssText + "\n";
            }
          }
        });
      } catch (e) {
        // Ignore CORS errors
      }
    });

    return cssText;
  }

  onMount(async () => {
    container = document.querySelector(".svelte-flow-container");

    await resetData();
    setData();
  });
</script>

<div
  class="canvas svelte-flow-container"
  style="width:{width}; height:{height}"
>
  <div style="grid-row: 1; grid-column:1; height:100%">
    <SvelteFlow
      {nodes}
      {edges}
      {nodeTypes}
      {edgeTypes}
      fitView
      minZoom={0.05}
      nodesDraggable={false}
      connectionMode={ConnectionMode.Loose}
      on:nodeclick={(event) => toggleInfo(event, event.detail.node.id)}
      on:openContextMenu={(event) =>
        openContextMenu(event, event.detail.node.id)}
      on:click={hideContextMenu}
      on:nodemouseenter={(event) => mouseover(event, event.detail.node.id)}
      on:nodemouseleave={(event) => mouseleave(event.detail.node.id)}
    >
      {#if seeMinimap}
        <MiniMap zoomable pannable height={120} />
      {/if}
    </SvelteFlow>
    <div
      style="display:{extrainfo ? 'inline-block' : 'none'}; width:{extrainfo
        ? '39%'
        : '0%'}; vertical-align:top; height:100%"
    >
      <slot {selectedNode} name="extraInfo" />
    </div>
    <div
      style="display:{subprocessMenuOpen
        ? 'inline-block'
        : 'none'}; width:{subprocessMenuOpen
        ? '39%'
        : '0%'}; vertical-align:top; height:100%"
    >
      <slot {selectedNode} name="nodeMenu" />
    </div>
  </div>
</div>
<div
  id="context-menu"
  bind:this={contextMenu}
  class={contextMenuVisible ? "visible" : ""}
>
  <div
    class="item"
    on:click={() => {
      subprocessMenuOpen = true;
      contextMenuVisible = false;
    }}
    on:keydown={() => {}}
  >
    Add to Activity
  </div>
</div>

<style>
  #context-menu {
    position: fixed;
    z-index: 10000;
    width: 150px;
    background: #1c1c20;
    border-radius: 5px;
    transform: scale(0);
    transform-origin: top left;
  }

  #context-menu.visible {
    transform: scale(1);
    transition: transform 200ms ease-in-out;
  }

  #context-menu .item {
    padding: 8px 10px;
    font-size: 15px;
    color: #eee;
    cursor: pointer;
    border-radius: inherit;
  }

  #context-menu .item:hover {
    background: #343434;
  }

  :global(.node rect) {
    stroke: var(--cds-text-01, #1c1c20);
    fill: var(--cds-layer, #f2f2f2);
    border-radius: 0.5rem;
  }

  :global(.edgePath path) {
    stroke: var(--cds-text-01, #1c1c20);
    fill: var(--cds-text-01, #1c1c20);
    stroke-width: 1.5px;
  }

  :global(g.node) {
    cursor: pointer;
  }

  :global(div.nodelabel) {
    display: flex !important;
    align-items: center;
  }

  .canvas {
    flex-grow: 1;
  }

  :global(.svelte-flow__node .node_hovered) {
    border: 2px solid #ff5f1f !important;
  }

  :global(.svelte-flow__node .node_hovered) {
    stroke: #ff5f1f !important;
  }

  :global(.svelte-flow__edge .edge_hovered) {
    stroke: #ff5f1f !important;
  }

  :global(.svelte-flow__edge .edge_hovered defs path) {
    stroke: #ff5f1f !important;
  }

  :global(.node_selected) {
    stroke: #5c25fa !important;
  }

  :global(foreignObject) {
    overflow: visible !important;
  }

  :global(.svelte-flow__attribution) {
    background-color: transparent !important;
  }
  :global(.svelte-flow__attribution a) {
    background-color: transparent !important;
    color: transparent !important;
  }
</style>
